import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Clock, Mic, Video, Key, Loader2, AlertCircle, PlayCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useEffect, useState } from 'react';
import Footer from '../components/common/Footer';

interface SessionRecord {
  id: string;
  start_time: string;
  end_time: string | null;
  duration: number | null;
  type: 'voice' | 'vision' | 'both';
  used_byok: boolean;
  created_at: string;
}

const History: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Debug logging
  console.log("History page - user:", user, "authLoading:", authLoading);
  
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSessions = async () => {
      if (!user) {
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Ensure user records exist first
        try {
          await supabase.rpc('ensure_user_records_exist', {
            p_user_id: user.id
          });
        } catch (setupError) {
          console.warn('Error ensuring user records for sessions:', setupError);
        }
        
        // Create a test session for demo purposes if none exist
        const { data: existingSessions } = await supabase
          .from('sessions')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        
        if (!existingSessions || existingSessions.length === 0) {
          // Insert a demo session
          await supabase
            .from('sessions')
            .insert({
              user_id: user.id,
              start_time: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
              end_time: new Date(Date.now() - 3300000).toISOString(), // 55 minutes ago
              duration: 300, // 5 minutes
              type: 'both',
              used_byok: false
            });
        }
        
        const { data, error } = await supabase
          .from('sessions')
          .select('id, start_time, end_time, duration, type, used_byok, created_at')
          .eq('user_id', user.id)
          .order('start_time', { ascending: false })
          .limit(30);

        if (error) {
          console.error('Error fetching sessions:', error);
          setError('Failed to load session history. Please try again.');
          setSessions([]);
        } else {
          const sessionData = data || [];
          setSessions(sessionData);
        }
      } catch (error) {
        console.error('Unexpected error:', error);
        setError('An unexpected error occurred. Please try again.');
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [user]);

  // Handle authentication state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading history...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Sign In Required</h2>
          <p className="text-gray-600 mb-6">
            You need to be signed in to view your session history.
          </p>
          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              fullWidth
            >
              Sign In
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/signup')}
              fullWidth
            >
              Create Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle authentication state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading history...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Sign In Required</h2>
          <p className="text-gray-600 mb-6">
            You need to be signed in to view your session history.
          </p>
          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              fullWidth
            >
              Sign In
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/signup')}
              fullWidth
            >
              Create Account
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  useEffect(() => {
    const fetchSessions = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('sessions')
          .select('id, start_time, end_time, duration, type, used_byok, created_at')
          .eq('user_id', user.id)
          .order('start_time', { ascending: false })
          .limit(30);

        if (error) {
          console.error('Error fetching sessions:', error);
          setError('Failed to load session history. Please try again.');
          setSessions([]);
        } else {
          setSessions(data || []);
        }
      } catch (error) {
        console.error('Unexpected error:', error);
        setError('An unexpected error occurred. Please try again.');
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [user]);

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-gray-50 py-8"
    >
      <div className="max-w-4xl mx-auto px-4">
      <motion.div variants={itemVariants} className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Session History</h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 mt-2">
          View your recent sessions (last 30 entries)
        </p>
      </motion.div>

      {loading ? (
        <motion.div variants={itemVariants}>
          <Card className="p-8 text-center">
            <Loader2 className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Loading Sessions
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Fetching your session history...
            </p>
          </Card>
        </motion.div>
      ) : error ? (
        <motion.div variants={itemVariants}>
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-error-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-error-700 mb-2">
              Error Loading History
            </h3>
            <p className="text-error-600 mb-4">{error}</p>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          </Card>
        </motion.div>
      ) : sessions.length === 0 ? (
        <motion.div variants={itemVariants}>
          <Card className="p-8 text-center">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
              No Sessions Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Start your first session to see your history here
            </p>
            <div className="mt-4">
              <Button
                variant="primary"
                onClick={() => navigate('/session')}
              >
                Start Session
              </Button>
            </div>
          </Card>
        </motion.div>
      ) : (
        <motion.div variants={itemVariants} className="space-y-4">
          {sessions.map((session) => (
            <Card key={session.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(session.start_time), 'MMM d, yyyy h:mm a')}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {session.duration ? formatDuration(session.duration) : 'In progress'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {(session.type === 'voice' || session.type === 'both') && (
                    <div className="text-primary-600 dark:text-primary-400">
                      <Mic className="h-5 w-5" />
                    </div>
                  )}
                  {(session.type === 'vision' || session.type === 'both') && (
                    <div className="text-primary-600 dark:text-primary-400">
                      <Video className="h-5 w-5" />
                    </div>
                  )}
                  {session.used_byok && (
                    <div className="text-secondary-600 dark:text-secondary-400">
                      <Key className="h-5 w-5" />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </motion.div>
      )}
      
      <Footer />
      </div>
    </motion.div>
  );
};

export default History;